import type { MediaItem } from "../types";

interface ApiEnvelope {
  ok: boolean;
  error?: string;
  [key: string]: unknown;
}

async function request<T extends ApiEnvelope>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, { credentials: "same-origin", ...init });
  const data = (await response.json().catch(() => ({ ok: false, error: "服务返回了未知格式" }))) as T;
  if (!response.ok || !data.ok) throw new Error(data.error || `请求失败 (${response.status})`);
  return data;
}

export const api = {
  async getItems(): Promise<MediaItem[]> {
    const collected: MediaItem[] = [];
    let offset: number | null = 0;
    while (offset !== null) {
      const data: ApiEnvelope & { items: MediaItem[]; nextOffset: number | null } = await request<ApiEnvelope & { items: MediaItem[]; nextOffset: number | null }>(`/api/items?limit=100&offset=${offset}`);
      collected.push(...data.items);
      offset = data.nextOffset;
    }
    return collected;
  },

  async getSession(): Promise<{ admin: boolean; configured: boolean }> {
    return request<ApiEnvelope & { admin: boolean; configured: boolean }>("/api/auth/me");
  },

  async login(password: string): Promise<void> {
    await request("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ password }),
    });
  },

  async logout(): Promise<void> {
    await request("/api/auth/logout", { method: "POST" });
  },

  async upload(files: File[], caption: string): Promise<void> {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));
    formData.set("caption", caption);
    await request("/api/items", { method: "POST", body: formData });
  },


  async importShare(text: string): Promise<{ title: string; assets: number; provider: string }> {
    return request<ApiEnvelope & { title: string; assets: number; provider: string }>("/api/import", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ text }),
    });
  },

  async deleteItem(id: string): Promise<void> {
    await request(`/api/items/${encodeURIComponent(id)}`, { method: "DELETE" });
  },

  async deleteItems(input: { ids?: string[]; all?: boolean }): Promise<number> {
    const remove = async (payload: { ids?: string[]; all?: boolean }) => {
      const data = await request<ApiEnvelope & { deleted: number }>("/api/items/bulk-delete", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      return data.deleted;
    };

    if (input.all) return remove({ all: true });
    const ids = input.ids ?? [];
    let deleted = 0;
    // The UI has no selection ceiling. Large selections are split only to keep
    // individual Worker requests and D1 batches comfortably below platform limits.
    for (let index = 0; index < ids.length; index += 1000) {
      deleted += await remove({ ids: ids.slice(index, index + 1000) });
    }
    return deleted;
  },
};
